<template>
  <div>
    <LabelledInput>
      <b-checkbox v-model="enable">
        Enable filter by category
        <BetaBadge />
      </b-checkbox>
    </LabelledInput>

    <template v-if="enable && !exclude.includes('settings')">
      <LabelledInput
        label="Field to filter by"
        helpful-tip="Choose the field by which users will be able to filter"
      >
        <b-select
          v-model="category"
          :options="options"
        />
      </LabelledInput>

      <LabelledInput
        label="Title"
        helpful-tip="Title for the input box"
      >
        <b-input v-model="title" />
      </LabelledInput>

      <LabelledInput
        label="Label"
        helpful-tip="Label displayed in the input box"
      >
        <b-input v-model="label" />
      </LabelledInput>

      <LabelledInput label="Legend position">
        <b-select
          v-model="position"
          :options="legendPositionOptions"
        />
      </LabelledInput>
    </template>
  </div>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import BetaBadge from '@/components/builder/UI/BetaBadge.vue'
import { FieldTypes, LegendPositions } from '@/types'
import { FILTER_BY_CATEGORY_KEY } from '@/constants'

const components = {
  LabelledInput,
  BetaBadge,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

// eslint-disable-next-line no-use-before-define
@Component<FilterByCategory>({
  components,
  watch: {
    enable (newValue: boolean) {
      // resets selectedCategory if disable
      if (!newValue) {
        this.selectedCategory = null
      }
    },
  },
})
export default class FilterByCategory extends Vue.extend({ props }) {
  legendPositionOptions = [
    { text: 'Top Left', value: LegendPositions.TOP_LEFT },
    { text: 'Top Right Button', value: LegendPositions.TOP_RIGHT_BUTTON },
    { text: 'Bottom Left', value: LegendPositions.BOTTOM_LEFT },
  ]

  get enable () {
    return this.$typedStore.primary.app.config[FILTER_BY_CATEGORY_KEY].enable
  }

  set enable (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [FILTER_BY_CATEGORY_KEY, 'enable'],
      value,
    })
  }

  get label () {
    return this.$typedStore.primary.app.config[FILTER_BY_CATEGORY_KEY].label
  }

  set label (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [FILTER_BY_CATEGORY_KEY, 'label'],
      value,
    })
  }

  get title () {
    return this.$typedStore.primary.app.config[FILTER_BY_CATEGORY_KEY].title
  }

  set title (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [FILTER_BY_CATEGORY_KEY, 'title'],
      value,
    })
  }

  get category () {
    return this.$typedStore.primary.app.config[FILTER_BY_CATEGORY_KEY].category
  }

  set category (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [FILTER_BY_CATEGORY_KEY, 'category'],
      value,
    })
  }

  get position () {
    return this.$typedStore.primary.app.config[FILTER_BY_CATEGORY_KEY].position
  }

  set position (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [FILTER_BY_CATEGORY_KEY, 'position'],
      value,
    })
  }

  get selectedCategory () {
    return this.$typedStore.public.search.filterByCategorySelected
  }

  set selectedCategory (selected) {
    this.$typedStore.public.search.setFilterByCategorySelected(selected)
  }

  get options () {
    return this.$typedStore.visualisationApp.fieldMap
      .filter(({ type }) => type === FieldTypes.STRING)
      .map(({ column, name }) => ({ value: column, text: name }))
  }
}

</script>
